import {
  LESSON_DRAFT_ID,
  LESSON_EXCLUDED_SHELF_IDS,
  LESSON_QUERY_KEY,
} from '@/dictionary';

/**
 * Работа страницы параграфа в контексте урока: страница открыта по ссылке
 * из урока (?lesson=<id>), поэтому материалы параграфа можно отмечать
 * и сохранять в урок.
 *
 * Пока урок не сохранён (?lesson=new) состав набирается для черновика,
 * и первое сохранение создаёт урок целиком одним POST.
 *
 * @param {object} source — { subject, paragraph } текущего параграфа
 */
export const useLessonSelection = (source) => {
  const route = useRoute();
  const router = useRouter();
  const toast = useToast();
  const draftStore = useLessonDraftStore();
  const { canEditLessons } = useLessonAccess();

  const lessonId = computed(() => route.query[LESSON_QUERY_KEY] ?? null);

  const isDraft = computed(() => lessonId.value === LESSON_DRAFT_ID);

  // учебники части полок (СПО) в уроки не набираются даже по прямой ссылке
  const isShelfAllowed = computed(
    () =>
      !LESSON_EXCLUDED_SHELF_IDS.includes(toValue(source).subject?.shelf_id),
  );

  // урок отдаётся только учителю-владельцу
  const isLessonContext = computed(
    () => !!lessonId.value && canEditLessons.value && isShelfAllowed.value,
  );

  const { lesson, saveLesson, updateParagraphMaterials } = useLesson(
    computed(() => (isDraft.value ? null : lessonId.value)),
  );
  const { createLesson } = useLessons();

  const isEditing = ref(false);
  const selectedMaterials = ref([]);

  /** Материалы этого параграфа, уже добавленные в урок */
  const lessonMaterials = computed(() => {
    const paragraphId = toValue(source).paragraph?.id;

    if (!paragraphId) return [];

    return (
      lesson.data?.value?.subjects
        ?.flatMap(({ paragraphs }) => paragraphs ?? [])
        ?.find(({ id }) => id === paragraphId)?.materials ?? []
    );
  });

  const selectedKeys = computed(() =>
    selectedMaterials.value.map(getLessonMaterialKey),
  );

  const isMaterialSelected = (material) =>
    selectedKeys.value.includes(getLessonMaterialKey(material));

  const toggleMaterial = (material) => {
    if (!isEditing.value) return;

    selectedMaterials.value = isMaterialSelected(material)
      ? selectedMaterials.value.filter(
          (selected) =>
            getLessonMaterialKey(selected) !== getLessonMaterialKey(material),
        )
      : [
          ...selectedMaterials.value,
          {
            model_type: material.model_type,
            model_subtype: material.model_subtype ?? null,
            id: material.id,
            title: material.title,
            slug: material.slug ?? null,
          },
        ];
  };

  /** Есть ли у параграфа материалы в уроке */
  const hasSelectedMaterials = computed(() => !!selectedMaterials.value.length);

  const getSelectedCount = (model) =>
    selectedMaterials.value.filter(
      (material) => getLessonMaterialType(material) === model,
    ).length;

  const isSaving = computed(
    () => saveLesson.isLoading.value || createLesson.isLoading.value,
  );

  const startEditing = () => {
    isEditing.value = true;
  };

  const cancelEditing = () => {
    selectedMaterials.value = [...lessonMaterials.value];
    isEditing.value = false;
  };

  const createFromDraft = (subject, paragraph) => {
    createLesson.mutate(
      {
        title: draftStore.title,
        materials: selectedMaterials.value.map((material) =>
          toLessonMaterialPayload(material, subject, paragraph),
        ),
      },
      {
        onSuccess: ({ data }) => {
          draftStore.clearDraft();
          isEditing.value = false;
          toast.success('Урок создан');
          router.replace({
            query: { ...route.query, [LESSON_QUERY_KEY]: data.id },
          });
        },
      },
    );
  };

  const saveSelection = () => {
    const { subject, paragraph } = toValue(source);

    if (isDraft.value) {
      // черновик не переживает перезагрузку: без названия возвращаем к его вводу
      if (!draftStore.hasTitle) {
        toast.error('Сначала укажите название урока');
        router.push(`/teacher/lessons/${LESSON_DRAFT_ID}`);
        return;
      }

      if (!selectedMaterials.value.length) {
        toast.error('Выберите материалы, чтобы создать урок');
        return;
      }

      createFromDraft(subject, paragraph);
      return;
    }

    updateParagraphMaterials(
      { subject, paragraph, materials: selectedMaterials.value },
      {
        onSuccess: () => {
          isEditing.value = false;
          toast.success('Изменения сохранены');
        },
      },
    );
  };

  watch(
    lessonMaterials,
    (materials) => {
      selectedMaterials.value = [...materials];
    },
    { immediate: true },
  );

  return {
    lessonId,
    isLessonContext,
    isEditing,
    isSaving,
    isMaterialSelected,
    toggleMaterial,
    hasSelectedMaterials,
    getSelectedCount,
    startEditing,
    cancelEditing,
    saveSelection,
  };
};
